import {boardCellKey, type BoardCell, type CellAddress} from '@github-ui/board-core'

import {normalizeItemMoves} from './board-move-axes'
import type {
  SavedViewsBoardData,
  SavedViewsBoardItem,
  SavedViewsBoardItemsResult,
  SavedViewsBoardPayload,
} from './types'

/**
 * Replaces refreshed cards, removes their old placements, and adds their current placements.
 * Counts change only in fully loaded cells and groups; unloaded occurrences cannot prove
 * previous membership, so their totals stay as the server last reported them.
 */
export function reconcileBoardItems<TItem extends SavedViewsBoardItem, TMetadata>(
  payload: SavedViewsBoardPayload<TItem, TMetadata>,
  result: SavedViewsBoardItemsResult<TItem>,
): SavedViewsBoardPayload<TItem, TMetadata> {
  const items = result.items.map(resolved => ({
    ...resolved,
    item: normalizeItemMoves(resolved.item, payload.boardData.rowField != null),
  }))
  const refreshed = new Map(items.map(resolved => [resolved.item.globalId, resolved]))
  const affected = new Set([...refreshed.keys(), ...result.removedIds])
  const destinations = new Map(
    items.map(resolved => [resolved.item.globalId, new Set(resolved.cells.map(boardCellKey))]),
  )
  const held = new Map<string, CellAddress[]>()

  for (const cell of payload.boardData.data.cells) {
    for (const item of cell.items) {
      if (!affected.has(item.globalId)) continue
      const addresses = held.get(item.globalId) ?? []
      addresses.push({columnKey: cell.columnKey, rowKey: cell.rowKey})
      held.set(item.globalId, addresses)
    }
  }

  const existingCells = new Set(payload.boardData.data.cells.map(boardCellKey))
  const cells = payload.boardData.data.cells.map(cell => reconcileCell(cell, refreshed, affected, destinations))

  for (const resolved of items) {
    for (const address of resolved.cells) {
      const key = boardCellKey(address)
      if (existingCells.has(key)) continue
      if (!payload.boardData.data.columns.some(column => column.key === address.columnKey)) continue
      if (!payload.boardData.data.rows.some(row => row.key === address.rowKey)) continue

      existingCells.add(key)
      const cellItems = items
        .filter(candidate => destinations.get(candidate.item.globalId)?.has(key))
        .map(candidate => candidate.item)
      // An omitted truncated cell may already contain other cards. Keep paging enabled until
      // its first page arrives, without treating the refreshed cards as an exact count.
      const itemsLoaded = payload.boardData.cellsTruncated !== true
      cells.push({
        ...address,
        items: cellItems,
        itemsLoaded,
        totalCount: itemsLoaded ? cellItems.length : undefined,
        hasMoreItems: !itemsLoaded,
      })
    }
  }

  return {
    ...payload,
    boardData: {
      ...payload.boardData,
      data: {
        ...payload.boardData.data,
        cells,
        columns: payload.boardData.data.columns.map(column => ({
          ...column,
          totalCount: isAxisComplete(payload.boardData, 'columnKey', column.key)
            ? adjustAxisCount(column.totalCount, column.key, 'columnKey', held, result)
            : column.totalCount,
        })),
        rows: payload.boardData.data.rows.map(row => ({
          ...row,
          totalCount: isAxisComplete(payload.boardData, 'rowKey', row.key)
            ? adjustAxisCount(row.totalCount, row.key, 'rowKey', held, result)
            : row.totalCount,
        })),
      },
    },
  }
}

function reconcileCell<TItem extends SavedViewsBoardItem>(
  cell: BoardCell<TItem>,
  refreshed: ReadonlyMap<string, SavedViewsBoardItemsResult<TItem>['items'][number]>,
  affected: ReadonlySet<string>,
  destinations: ReadonlyMap<string, ReadonlySet<string>>,
): BoardCell<TItem> {
  const key = boardCellKey(cell)
  const previouslyHeld = new Set(cell.items.filter(item => affected.has(item.globalId)).map(item => item.globalId))
  const nextItems = cell.items.flatMap(item => {
    if (!affected.has(item.globalId)) return [item]
    const replacement = refreshed.get(item.globalId)
    return replacement && destinations.get(item.globalId)?.has(key) ? [replacement.item] : []
  })

  for (const [id, resolved] of refreshed) {
    if (!previouslyHeld.has(id) && destinations.get(id)?.has(key)) nextItems.push(resolved.item)
  }

  const nextHeld = new Set(nextItems.filter(item => affected.has(item.globalId)).map(item => item.globalId))
  const countDelta = nextHeld.size - previouslyHeld.size

  return {
    ...cell,
    items: nextItems,
    ...(cell.totalCount === undefined || !isCellComplete(cell)
      ? {}
      : {totalCount: Math.max(0, cell.totalCount + countDelta)}),
  }
}

function isCellComplete(cell: BoardCell<unknown>): boolean {
  return cell.itemsLoaded !== false && !cell.hasMoreItems
}

function isAxisComplete(
  {data, cellsTruncated}: SavedViewsBoardData<SavedViewsBoardItem>,
  axis: keyof CellAddress,
  key: string,
): boolean {
  const values = axis === 'columnKey' ? data.columns : data.rows
  if (values.find(value => value.key === key)?.totalCount === 0) return true
  const otherValues = axis === 'columnKey' ? data.rows : data.columns
  const hasMore =
    axis === 'columnKey' ? data.hasMoreRows || data.rowsTruncated : data.hasMoreColumns || data.columnsTruncated
  if (hasMore) return false
  const otherAxis = axis === 'columnKey' ? 'rowKey' : 'columnKey'
  const cells = new Map(data.cells.filter(cell => cell[axis] === key).map(cell => [cell[otherAxis], cell]))
  return otherValues.every(value => {
    const cell = cells.get(value.key)
    return cell ? isCellComplete(cell) : !cellsTruncated
  })
}

function adjustAxisCount<TItem extends SavedViewsBoardItem>(
  count: number | undefined,
  key: string,
  axis: keyof CellAddress,
  held: ReadonlyMap<string, readonly CellAddress[]>,
  result: SavedViewsBoardItemsResult<TItem>,
): number | undefined {
  if (count === undefined) return undefined

  let delta = 0
  const current = new Map(result.items.map(resolved => [resolved.item.globalId, resolved.cells]))
  for (const id of [...current.keys(), ...result.removedIds]) {
    const wasHeld = held.get(id)?.some(address => address[axis] === key) ?? false
    const isHeld = current.get(id)?.some(address => address[axis] === key) ?? false
    if (wasHeld !== isHeld) delta += isHeld ? 1 : -1
  }

  return Math.max(0, count + delta)
}
